import React from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Slide,
  TextField,
  Typography,
  useTheme,
  Link,
  Divider,
  Box,
  ButtonBase,
  Collapse,
} from '@mui/material';
import { prettifyRut } from 'react-rut-formatter';
import { isMobile } from 'react-device-detect';
import { TransitionProps } from '@mui/material/transitions';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import VerificationInput from 'react-verification-input';
import QRCode from 'qrcode.react';
import claveUnicaStyles, { styles } from './styles';
import AlertDialog from '../AlertDialog';
import LoadingAvatar from '../LoadingAvatar';
import CannotCompleteVerificationDialog from '../CannotCompleteVerificationDialog';
import analytics from '../../utils/analytics';
import getPartnerButton from '../PartnerButtons';
import { Partner } from '../LeadFormIndex/types';
import InfoDialog from '../InfoDialog';
import { plr, SHORT_DESKTOP } from '../../feature/preloan/v3-redesign/landing/tokens';
import { PlrButton, PlrFieldLabel, plrInputSx } from '../../feature/preloan/v3-redesign/landing/ui';
import {
  chevronDownIcon, eyeIcon, eyeOffIcon, shieldCheckIcon,
} from '../../feature/preloan/v3-redesign/landing/icons';
import { isBancamePartner } from '../../feature/preloan/v3-redesign/landing/PlrShell';
import {
  CU_CONSENT_TEXT, CU_SUMMARY, cuRecipientDetail,
} from '../../feature/preloan/v3-redesign/legal';

// Checkbox + texto del rediseño (2g). Todo el texto es clickeable (label), salvo los links.
function ConsentRow({
  testId, checked, onToggle, disabled, children,
}: {
  testId: string,
  checked: boolean,
  onToggle: () => void,
  disabled: boolean,
  children: React.ReactNode,
}) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        fontSize: { xs: 13.5, sm: 14 },
        lineHeight: 1.45,
        color: plr.ink,
        cursor: 'pointer',
      }}
    >
      <Checkbox
        data-pw={testId}
        checked={checked}
        onClick={onToggle}
        disabled={disabled}
        disableRipple
        sx={{
          p: 0,
          mt: '-1px',
          color: plr.faint,
          '&.Mui-checked': { color: plr.ink },
          '& .MuiSvgIcon-root': { fontSize: 24 },
        }}
      />
      <span>{children}</span>
    </Box>
  );
}

const inlineLinkSx = {
  font: 'inherit',
  color: plr.ink,
  fontWeight: 600,
  textDecoration: 'underline',
  verticalAlign: 'baseline',
  '&:hover': { color: plr.inkHover },
} as const;

const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
  // eslint-disable-next-line react/jsx-props-no-spreading
) => <Slide direction="up" ref={ref} {...props} />);

export function getTimeRemaining(endDate: Date) {
  const total = endDate.getTime() - new Date().getTime();
  if (total <= 0) {
    return '00:00';
  }
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / (1000 * 60)) % 60);

  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');

  return `${formattedMinutes}:${formattedSeconds}`;
}

interface ClaveUnicaDialogInterface {
  rut: string,
  action: (password: string, code?: string) => Promise<number>,
  maximumTries: boolean,
  triggerSubmit: boolean,
  button: React.ReactElement|null,
  subtitle: string,
  origin: string,
  needAuthFactor: boolean,
  pageCall?: {category: string, name: string},
  customLogo?: string,
  customLoadingLogo?: string,
  partner?: Partner,
  email?: string,
  facialAuthFactor?: boolean,
  urlFacialAuthFactor?: string,
  fallback?: () => Promise<number>,
  fallbackUnemployed?: () => Promise<void>,
  fallbackManualEvaluation?: () => Promise<void>,
  // 'inline': formulario dentro del paso (flujo PLR rediseñado, 2g).
  variant?: 'dialog' | 'inline',
  onChangeRut?: () => void,
  submitLabel?: string,
  // Solo inline: el paso muestra su propia pantalla de espera (3f) mientras carga.
  onLoadingChange?: (loading: boolean) => void,
}
export default function ClaveUnicaDialogRedesign(props: ClaveUnicaDialogInterface) {
  const theme = useTheme();
  const {
    rut, action, maximumTries, triggerSubmit, button,
    subtitle, origin, pageCall, customLogo, needAuthFactor, facialAuthFactor,
    customLoadingLogo, urlFacialAuthFactor,
    partner = {
      partnerName: 'Banca.me',
      config: {
        styles: {
          primaryColor: theme.palette.primary.main,
          secondaryColor: theme.palette.secondary.main,
        },
      },
      products: ['microLoan', 'consumer'],
    },
    email,
    fallback,
    fallbackUnemployed,
    fallbackManualEvaluation,
    variant = 'dialog',
    onChangeRut,
    submitLabel,
    onLoadingChange,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [checked2, setChecked2] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [codeError, setCodeError] = React.useState(false);
  const [facialAuthFactorError, setFacialAuthFactorError] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [timer, setTimer] = React.useState('00:00');
  const [facialAuthFactorTimer, setFacialAuthFactorTimer] = React.useState<string>('');
  const [expTime, setExpTime] = React.useState(new Date().getTime() + 300000);
  const [retry, setRetry] = React.useState(0);
  const [whyOpen, setWhyOpen] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((o) => !o);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const classes = claveUnicaStyles();

  const handleClickOpen = () => {
    if (pageCall && !needAuthFactor) {
      analytics.page(pageCall.category, pageCall.name);
    } else if (pageCall && needAuthFactor) {
      analytics.page(pageCall.category, 'DIALOGO 2FA CU');
    }
    setOpen(true);
  };

  const handleCodeChange = (value: string) => {
    setCode(value.toUpperCase());
  };

  const handleClose = () => {
    setOpen(false);
  };
  const goToFogotPassword = () => {
    analytics.track('Click Link', {
      text: '¿Olvidaste tu ClaveÚnica?',
      initiator: 'usuario',
    });
    // Sitio oficial: nunca un formulario propio de recuperación.
    window.open('https://claveunica.gob.cl/recuperar', '_blank', 'noopener,noreferrer');
  };
  const onSubmit = async () => {
    setLoading(true);
    setError(false);
    if (!facialAuthFactor && !needAuthFactor) {
      analytics.track('Click Boton', { text: 'Continuar', initiator: 'usuario' });
    }
    if (!needAuthFactor && (!checked || !checked2 || password === '')) {
      setLoading(false);
      return setError(true);
    }
    if (needAuthFactor && code === '' && !facialAuthFactor) {
      setLoading(false);
      return setError(true);
    }
    try {
      let res;
      if (facialAuthFactor) {
        res = await action(password, code);
        if (res === 208) {
          setFacialAuthFactorError(true);
        }
      } else if (needAuthFactor) {
        res = await action(password, code);
        if (res === 207) {
          setCodeError(true);
          setRetry(retry + 1);
        }
      } else {
        analytics.page('PLR - ONBOARDING', 'CU INGRESADA');
        res = await action(password);
      }
      if (res !== 207 && res !== 208) {
        handleClose();
      }
    } catch (e) {
      setError(true);
    }
    return setLoading(false);
  };

  const handleSubmit2FA = async () => {
    analytics.track('Click Boton', {
      text: facialAuthFactor ? 'Ya completé la verificación' : 'Confirmar',
      initiator: 'usuario',
    });
    onSubmit();
  };

  const handleSubmitFallback = async () => {
    if (!fallback) {
      return;
    }
    setLoading(true);
    setError(false);
    analytics.track('Click Boton', {
      text: 'No pude completar la verificación',
      initiator: 'usuario',
    });
    const res = await fallback();
    if (res !== 200) {
      setError(true);
      setLoading(false);
      return;
    }
    handleClose();
    setLoading(false);
  };

  const handleResendCode = async () => {
    analytics.track('Click Boton', {
      text: 'Reenviar código',
      initiator: 'usuario',
    });
    setLoading(true);
    setError(false);
    if (!needAuthFactor) {
      setError(true);
      return setLoading(false);
    }
    try {
      const res = await action(password, code);
      if (res !== 207) {
        return setError(true);
      }
      setCodeError(false);
      setCode('');
      setExpTime(new Date().getTime() + 300000);
      setTimer('05:00');
    } catch (e) {
      setError(true);
    }
    return setLoading(false);
  };

  React.useEffect(() => {
    if (triggerSubmit) {
      setOpen(true);
      setTimeout(() => onSubmit(), 1000);
    }
  }, [triggerSubmit]);

  const handleCheckBox = () => {
    analytics.track('Click Checkbox', {
      text: 'Acepto los TyC y política privacidad',
      initiator: 'usuario',
    });
    setChecked((o) => !o);
  };
  const handleCheckBox2 = () => {
    analytics.track('Click Checkbox', {
      text: 'Autorizo a usar mi clave única',
      initiator: 'usuario',
    });
    setChecked2((o) => !o);
  };
  const handleClickWhy = () => {
    if (!whyOpen) {
      analytics.track('Click Boton', {
        text: 'Para qué usamos tu ClaveÚnica',
        initiator: 'usuario',
      });
    }
    setWhyOpen((o) => !o);
  };
  React.useEffect(() => {
    if (variant === 'inline') onLoadingChange?.(loading);
  }, [loading]);
  const handleClickTermsAndConditions = () => {
    analytics.track('Click Link', {
      text: 'términos y condiciones',
      initiator: 'usuario',
    });
  };
  const handleClickPrivacyPolicy = () => {
    analytics.track('Click Link', {
      text: 'política de privacidad',
      initiator: 'usuario',
    });
  };

  const handleClickFacialAuthFactor = () => {
    analytics.track('Click Boton', {
      text: 'Iniciar verificación',
      initiator: 'usuario',
    });
    setExpTime(new Date().getTime() + 20000);
    setFacialAuthFactorTimer('00:20');
    window.open(urlFacialAuthFactor, '_blank');
  };

  React.useEffect(() => {
    if (needAuthFactor && !facialAuthFactor) {
      analytics.page(pageCall?.category || 'PLR - ONBOARDING', 'DIALOGO 2FA CU');
      setTimer('05:00');
      setExpTime(new Date().getTime() + 300000);
    }
  }, [needAuthFactor]);

  React.useEffect(() => {
    if (timer !== '00:00') {
      const remaining = getTimeRemaining(new Date(expTime));
      setTimeout(() => setTimer(remaining), 1000);
    }
  }, [timer]);

  React.useEffect(() => {
    if (facialAuthFactorTimer !== '00:00' && facialAuthFactorTimer !== '') {
      const remaining = getTimeRemaining(new Date(expTime));
      setTimeout(() => setFacialAuthFactorTimer(remaining), 1000);
    }
  }, [facialAuthFactorTimer]);

  React.useEffect(() => {
    if (facialAuthFactor && !isMobile) {
      analytics.page(pageCall?.category || 'PLR - ONBOARDING', 'VERIFICACION FACIAL AFC DESKTOP');
      setExpTime(new Date().getTime() + 25000);
      setFacialAuthFactorTimer('00:25');
    } else if (facialAuthFactor && isMobile) {
      analytics.page(pageCall?.category || 'PLR - ONBOARDING', 'VERIFICACION FACIAL AFC MOBILE');
    }
  }, [facialAuthFactor]);

  React.useEffect(() => {
    if (retry > 3) {
      analytics.page(pageCall?.category || 'PLR - ONBOARDING', 'DIALOGO 2FA CU BLOQUEADA');
    }
  }, [retry]);

  let body;

  if (retry > 3) {
    body = (
      <Box sx={styles.box2FA}>
        <Typography variant="h6" align="center" fontWeight="bold" color="secondary" gutterBottom sx={{ marginBlock: 5 }}>
          Has excedido el número de intentos permitidos
        </Typography>
        <Typography variant="body1" align="center" gutterBottom>
          Puedes reintentar en 1 hora.
        </Typography>
      </Box>
    );
  } else if (facialAuthFactor && needAuthFactor) {
    body = (
      <Box sx={styles.box2FA}>
        <Typography align="center" variant="h5" fontWeight="bold" gutterBottom sx={{ paddingInline: 5, marginTop: 5 }}>
          Verificación facial AFC
        </Typography>
        <Typography variant="body2" align="center" gutterBottom>
          La Administradora de Fondos de Cesantía
          {' '}
          <b>(AFC)</b>
          {' '}
          requiere verificación de identidad para obtener tus cotizaciones.
        </Typography>
        {isMobile && (
          <img src="https://assets.banca.me/icons/6.png" width={40} alt="logo" />
        )}
        {!isMobile && (
          <Box sx={{
            width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', marginBlock: 1,
          }}
          >
            <QRCode
              fgColor="#01182e"
              level="L"
              size={150}
              value={urlFacialAuthFactor || ''}
            />
          </Box>
        )}
        <Typography variant="body1" color="secondary" align="center" gutterBottom sx={{ marginTop: 2 }}>
          {isMobile
            ? (
              <>
                Cuando termines,
                {' '}
                <b>debes regresar a esta página</b>
                {' '}
                para continuar con tu solicitud.
              </>
            )
            : (
              <>
                <b>Debes regresar a esta página</b>
                {' '}
                para continuar con tu solicitud una vez escaneado el código QR
                {' '}
                y completada la verificación.
              </>
            )}
        </Typography>
        {isMobile && <Typography variant="body2" align="center">Haz click en Iniciar verificación.</Typography>}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center', marginTop: 7.5,
        }}
        >
          {isMobile && (
          <Button
            data-pw="cu-2fa-submit-button"
            variant={facialAuthFactorTimer !== '00:00' ? 'contained' : 'outlined'}
            onClick={handleClickFacialAuthFactor}
            disabled={loading}
          >
            Iniciar verificación
          </Button>
          )}
          <Button
            data-pw="cu-2fa-resend-button"
            onClick={handleSubmit2FA}
            variant={facialAuthFactorTimer === '00:00' ? 'contained' : 'outlined'}
            color="primary"
            disabled={facialAuthFactorTimer !== '00:00'}
          >
            Ya completé la verificación
          </Button>
          {(fallback && facialAuthFactorTimer === '00:00') && (
            <Button
              data-pw="cu-2fa-fallback-button"
              onClick={handleSubmitFallback}
              variant="text"
              disabled={loading || facialAuthFactorTimer !== '00:00'}
            >
              <Typography variant="caption" align="center" sx={{ textDecoration: 'underline' }}>
                No pude completar la verificación
              </Typography>
            </Button>
          )}
        </div>
        {facialAuthFactorError && (
          <Typography variant="caption" color="error" align="center" gutterBottom sx={{ paddingInline: 1, marginTop: 1.5 }}>
            La verificación facial aún no ha sido completada.
          </Typography>
        )}
      </Box>
    );
  } else if (needAuthFactor) {
    body = (
      <>
        <Box sx={styles.box2FA}>
          <Typography
            variant="caption"
            color="secondary"
            align="center"
            gutterBottom
            sx={{
              paddingInline: 5,
              paddingBlock: 0.5,
              backgroundColor: theme.palette.background.defaultSecondary,
              borderRadius: 5,
              marginInline: 'auto',
            }}
          >
            {timer}
          </Typography>
        </Box>
        <Box sx={styles.box2FA}>
          <Typography align="center" variant="h6" fontWeight="bold" gutterBottom sx={{ paddingInline: 5 }}>
            Código validación ClaveÚnica
          </Typography>
          <Typography variant="body2" color="secondary" align="center">
            <b>ClaveÚnica</b>
            {' '}
            requiere un segundo factor de autenticación
          </Typography>

          <img src="https://assets.banca.me/icons/13.png" width={40} alt="logo" />
          <Typography variant="body2" align="center" sx={{ paddingInline: 1, marginBottom: -1.5, marginTop: 1.5 }}>
            Ingresa el código que te llegó al correo
            {' '}
            <b>{email || ''}</b>
          </Typography>
          <InfoDialog
            dialogContent={(
              <>
                <Typography variant="body1" align="center" sx={{ paddingInline: 2.5, marginTop: 2.5 }}>
                  Para actualizar tu correo debes iniciar sesión con tu ClaveÚnica en
                  {' '}
                  <a
                    onClick={() => analytics.track('Click Link', {
                      text: 'claveunica.gob.cl',
                      initiator: 'usuario',
                    })}
                    href=" https://claveunica.gob.cl/"
                    target="_blank"
                  >
                    claveunica.gob.cl
                  </a>
                  {' '}
                  e ir a sección “Mi perfil”.
                </Typography>
                <br />
                <Typography variant="body2" color="primary" align="center" sx={{ paddingInline: 2.5 }}>
                  Debes tener a mano el número de documento de tu cédula de identidad.
                </Typography>
              </>
            )}
            button={(
              <Button
                variant="text"
                color="primary"
                onClick={() => analytics.track('Click Boton', {
                  text: '¿No es tu correo?',
                  initiator: 'usuario',
                })}
              >
                <Typography variant="caption" align="center" style={{ textDecoration: 'underline' }}>¿No es tu correo?</Typography>
              </Button>
            )}
            maxWidth="xs"
          />
          <VerificationInput
            inputProps={{ autoComplete: 'off', autoFocus: true }}
            value={code}
            onChange={handleCodeChange}
            autoFocus
            classNames={{
              container: classes.container,
              character: classes.caracter,
              characterFilled: classes.caracterFilled,
            }}
          />
          <Typography variant="caption" align="center" sx={{ paddingInline: 1, marginTop: 1 }}>
            Por favor revisa tu
            {' '}
            <b>correo electrónico</b>
          </Typography>

          {(codeError || timer === '00:00') && (
          <Typography variant="caption" color="error" align="center" gutterBottom sx={{ paddingInline: 1 }}>
            {timer === '00:00' ? 'El código ha expirado' : 'El código ingresado es incorrecto'}
          </Typography>
          )}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center', marginTop: 5,
          }}
          >
            <Button
              data-pw="cu-2fa-submit-button"
              variant="contained"
              onClick={handleSubmit2FA}
              disabled={loading || code.length !== 6 || timer === '00:00'}
            >
              Confirmar
            </Button>
            <Button
              data-pw="cu-2fa-resend-button"
              onClick={handleResendCode}
              variant="outlined"
              color="secondary"
              disabled={timer !== '00:00'}
            >
              Reenviar código
            </Button>
            {fallbackUnemployed !== undefined && fallbackManualEvaluation !== undefined && (
              <CannotCompleteVerificationDialog
                title="¿No puedes completar la verificación?"
                subtitle="Si no puedes completar la verificación, puedes continuar seleccionando alguna de las siguientes opciones:"
                buttonText="No puedo ingresar el código"
                onUnemployedClick={fallbackUnemployed}
                onManualEvaluationClick={fallbackManualEvaluation}
                pageCall={{
                  category: pageCall?.category || 'PLR - ONBOARDING',
                  name: 'DIALOGO NO PUEDE COMPLETAR VERIFICACION',
                }}
              />
            )}
          </div>
          {retry > 0 && (
          <Typography component="div" variant="caption" style={{ opacity: 1, marginTop: 15, textAlign: 'center' }} gutterBottom>
            <b>
              Intentos:
              {' '}
              {retry + 1}
              {' '}
              / 4
            </b>
            <div style={{ marginTop: 10 }}>
              <i>
                Si te equivocas 4 veces, se bloqueará tu ClaveÚnica durante 1 hora
              </i>
            </div>
          </Typography>
          )}
        </Box>
      </>
    );
  } else if (variant === 'inline') {
    body = (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '14px', sm: '16px' },
        flex: 1,
        [SHORT_DESKTOP]: { gap: '12px' },
      }}
      >
        <Box
          component="p"
          data-pw="cu-rut"
          sx={{ m: 0, fontSize: { xs: 14, sm: 15 }, color: plr.body }}
        >
          RUT
          {' '}
          <Box component="b" sx={{ color: plr.ink, fontVariantNumeric: 'tabular-nums' }}>
            {prettifyRut(rut)}
          </Box>
          {onChangeRut && <span aria-hidden="true"> · </span>}
          {onChangeRut && (
            <ButtonBase data-pw="cu-change-rut" onClick={onChangeRut} sx={inlineLinkSx}>
              Cambiar
            </ButtonBase>
          )}
        </Box>
        <Box sx={{ background: plr.surface, borderRadius: '18px', px: '16px' }}>
          <ButtonBase
            data-pw="cu-why"
            aria-expanded={whyOpen}
            aria-controls="cu-why-panel"
            onClick={handleClickWhy}
            sx={{
              width: '100%',
              minHeight: 64,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              py: '10px',
              textAlign: 'left',
              fontFamily: plr.font,
              color: plr.ink,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: '50%',
                background: plr.chip,
                color: plr.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {shieldCheckIcon({ size: 17 })}
            </Box>
            <Box
              component="span"
              sx={{
                flex: 1, display: 'flex', flexDirection: 'column', gap: '2px',
              }}
            >
              <Box component="b" sx={{ fontSize: 14 }}>Para qué usamos tu ClaveÚnica</Box>
              <Box
                component="span"
                data-pw="cu-recipient"
                sx={{ fontSize: 12.5, lineHeight: 1.4, color: plr.body }}
              >
                {CU_SUMMARY}
              </Box>
            </Box>
            <Box
              component="span"
              sx={{
                display: 'flex',
                transition: 'transform .15s',
                transform: whyOpen ? 'rotate(180deg)' : 'none',
              }}
            >
              {chevronDownIcon({ size: 16 })}
            </Box>
          </ButtonBase>
          <Collapse in={whyOpen}>
            <Box
              component="ol"
              id="cu-why-panel"
              sx={{
                m: 0,
                p: '2px 0 14px 18px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                fontSize: 13,
                lineHeight: 1.45,
                color: plr.body,
                '& b': { color: plr.ink },
              }}
            >
              <li>
                <b>La recibe banca.me</b>
                {cuRecipientDetail(
                  isBancamePartner(partner.partnerName) ? undefined : partner.partnerName?.trim(),
                )}
              </li>
              <li>
                <b>Leemos</b>
                {' '}
                tus ingresos, cotizaciones y deudas. No hacemos trámites ni cambios.
              </li>
              <li>
                <b>Calculamos</b>
                {' '}
                cuánto podrías financiar y te mostramos el resultado.
              </li>
            </Box>
          </Collapse>
        </Box>
        <Box sx={{
          display: 'flex', flexDirection: 'column', gap: '6px', mt: '2px',
        }}
        >
          <PlrFieldLabel htmlFor="cu-password">Tu ClaveÚnica</PlrFieldLabel>
          <TextField
            id="cu-password"
            data-pw="cu-input"
            placeholder="Ingresa tu ClaveÚnica"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            error={error && password === ''}
            helperText={(error && password === '') ? 'Ingresa tu ClaveÚnica' : undefined}
            disabled={loading}
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Ocultar clave' : 'Mostrar clave'}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{ color: plr.muted, borderRadius: '12px' }}
                  >
                    {showPassword ? eyeOffIcon() : eyeIcon()}
                  </IconButton>
                </InputAdornment>),
            }}
            sx={plrInputSx}
          />
          <ButtonBase
            onClick={goToFogotPassword}
            aria-label="¿La olvidaste? Recupérala en el sitio oficial claveunica.gob.cl"
            sx={{ ...inlineLinkSx, alignSelf: 'flex-end', fontSize: 13 }}
          >
            ¿La olvidaste? Ir a claveunica.gob.cl
          </ButtonBase>
        </Box>
        {/* Dos consentimientos separados, como el flujo anterior (Max, 29 Oct). Pegados al
            footer (junto con "Ver mi resultado") cuando sobra alto en mobile. */}
        <Box sx={{
          mt: 'auto', display: 'flex', flexDirection: 'column', gap: '12px',
        }}
        >
          <ConsentRow
            testId="cu-optin"
            checked={checked}
            onToggle={handleCheckBox}
            disabled={loading}
          >
            Acepto los
            {' '}
            <a
              onClick={handleClickTermsAndConditions}
              href={`/terms-and-conditions?origin=${origin}&referrer=plr`}
              target="_blank"
              rel="noopener noreferrer"
            >
              términos y condiciones
            </a>
            {' '}
            y la
            {' '}
            <a
              onClick={handleClickPrivacyPolicy}
              href="https://www.banca.me/politicas-de-privacidad"
              target="_blank"
              rel="noopener noreferrer"
            >
              política de privacidad
            </a>
            .
          </ConsentRow>
          <ConsentRow
            testId="cu-optin-2"
            checked={checked2}
            onToggle={handleCheckBox2}
            disabled={loading}
          >
            {CU_CONSENT_TEXT}
          </ConsentRow>
        </Box>
        {(error && (!checked || !checked2)) && (
          <Box component="p" role="alert" sx={{ m: '-4px 0 0', fontSize: 13, color: '#C0392B' }}>
            Para continuar, acepta los términos y autoriza el uso de tu ClaveÚnica.
          </Box>
        )}
        <PlrButton
          testId="cu-submit-button"
          onClick={onSubmit}
          disabled={loading || maximumTries}
        >
          {maximumTries ? 'Máximo de intentos permitidos' : (submitLabel || 'Continuar')}
        </PlrButton>
      </Box>
    );
  } else {
    body = (
      <>
        <Typography
          variant="h5"
          align="center"
          sx={{ marginBottom: 2.5, marginTop: 2 }}
        >
          Ingreso con ClaveÚnica
        </Typography>
        <Typography
          align="center"
          // fontStyle="italic"
          sx={{ color: partner.config.styles?.secondaryColor, marginBottom: 3.5 }}
        >
          {subtitle}
        </Typography>
        <Typography sx={{ marginBottom: 1 }}>
          RUT
        </Typography>
        <TextField
          style={{ marginBottom: 10 }}
          value={prettifyRut(rut)}
          disabled
          variant="outlined"
        />
        <Box sx={{ display: 'flex', placeContent: 'space-between', marginBottom: 1 }}>
          <Typography>
            Contraseña
          </Typography>
          <Box
            onClick={goToFogotPassword}
            className={classes.passwordForgotten}
            sx={{ height: '100%', color: partner.config.styles?.secondaryColor, fontSize: { xs: '12px', sm: '14px' } }}
            aria-hidden
          >
            ¿Olvidaste tu ClaveÚnica?
          </Box>
        </Box>
        <TextField
          data-pw="cu-input"
          style={{ marginBottom: 20 }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type={showPassword ? 'text' : 'password'}
          placeholder="Clave Única"
          variant="outlined"
          error={error && password === ''}
          helperText={(error && password === '') ? 'Por favor ingrese su contraseña' : ''}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>),
          }}
        />
        <FormControlLabel
          control={(
            <Checkbox
              data-pw="cu-optin"
              size="small"
              checked={checked}
              onClick={handleCheckBox}
              disabled={loading}
              style={{ paddingBlock: '5px' }}
            />
            )}
          label={(
            <Typography variant="caption" component="div">
              Acepto
              {' '}
              <Link
                onClick={handleClickTermsAndConditions}
                style={{ textDecoration: 'underline', color: partner.config.styles?.secondaryColor }}
                href={`/terms-and-conditions?origin=${origin}&referrer=plr`}
                target="_blank"
                rel="noopener"
              >
                términos y condiciones
              </Link>
              {' '}
              y
              {' '}
              <Link
                onClick={handleClickPrivacyPolicy}
                style={{
                  textDecoration: 'underline',
                  color: partner.config.styles?.secondaryColor,
                }}
                href="https://www.banca.me/politicas-de-privacidad"
                target="_blank"
                rel="noopener"
              >
                política de privacidad
              </Link>
            </Typography>
            )}
        />
        <FormControlLabel
          control={(
            <Checkbox
              data-pw="cu-optin-2"
              size="small"
              checked={checked2}
              onClick={handleCheckBox2}
              disabled={loading}
              style={{ paddingBlock: '5px' }}
            />
            )}
          label={(
            <Typography variant="caption" component="div">
              Autorizo a usar mi ClaveÚnica
              {' '}
              <Typography variant="caption" component="span" color="primary" fontWeight="bold">
                (sin almacenarla)
              </Typography>
              {' '}
              para consultar
              {' '}
              <b>SII, AFC y SUSESO</b>
              {' '}
              con el fin de validar mi capacidad de crédito.
            </Typography>
            )}
        />
        <div style={{ textAlign: 'center', marginBottom: 5, marginTop: 7.5 }}>
          {
          getPartnerButton({
            partner,
            disabled: loading || maximumTries,
            onClick: onSubmit,
            buttonLabel: maximumTries ? 'Máximo de intentos permitidos' : (submitLabel || 'Continuar'),
            testingLabel: 'cu-submit-button',
          })
      }

        </div>
        <Typography
          align="center"
          gutterBottom
          fontStyle="italic"
          style={{ paddingTop: '0.5em', color: partner.config.styles?.secondaryColor }}
        >
          Utilizamos tecnología de encriptación bancaria AES 256 para la comunicación de datos.
          No se guardarán tus credenciales.
        </Typography>
        {(error && (!checked || !checked2)) && (
        <Typography
          variant="caption"
          color="error"
          component="div"
          align="center"
          style={{ marginTop: 5 }}
        >
          Por favor acepte los términos y condiciones, y la autorización para usar su ClaveÚnica.
        </Typography>
        )}
      </>
    );
  }

  if (variant === 'inline') {
    return (
      <Box
        data-pw="cu-inline"
        sx={{
          fontFamily: plr.font, flex: 1, display: 'flex', flexDirection: 'column',
        }}
      >
        {body}
        {!onLoadingChange && (
          <AlertDialog
            hideLogo
            triggerClose={!loading}
            isTriggered={loading}
            button={<div />}
            hideCloseButton
            hideSubmitButton
            dialogContent={<LoadingAvatar customLogo={customLoadingLogo} loading={loading} />}
          />
        )}
      </Box>
    );
  }

  return (
    <>
      {button ? (
        React.cloneElement(button, {
          onClick: (e: React.SyntheticEvent) => { e.preventDefault(); handleClickOpen(); },
        })
      )
        : (
          <Button
            onClick={handleClickOpen}
            variant="contained"
          >
            Ingresar ClaveÚnica
          </Button>
        )}
      <Dialog
        className={classes.dialog}
        open={open}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="clave-unica-dialog"
        maxWidth="sm"
      >
        <DialogContent style={{ paddingBottom: 10 }}>
          {customLogo && (
            <>
              <div style={{ marginBottom: 10, textAlign: 'center' }}>
                <object
                  data={customLogo}
                  aria-labelledby="banca.me logo"
                  height="30px"
                />
              </div>

              <Divider />
            </>
          )}
          {body}
          <AlertDialog
            hideLogo
            triggerClose={!loading}
            isTriggered={loading}
            button={<div />}
            hideCloseButton
            hideSubmitButton
            dialogContent={<LoadingAvatar customLogo={customLoadingLogo} loading={loading} />}
          />
        </DialogContent>
        <DialogActions>
          {!needAuthFactor && (
            <Button
              onClick={handleClose}
              disabled={loading}
            >
              Cerrar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

ClaveUnicaDialogRedesign.defaultProps = {
  pageCall: undefined,
  customLogo: undefined,
  customLoadingLogo: undefined,
  partner: {
    partnerName: 'Banca.me',
    config: {},
    products: ['microLoan', 'consumer'],
  },
  email: undefined,
  facialAuthFactor: false,
  urlFacialAuthFactor: '',
  fallback: undefined,
  fallbackUnemployed: undefined,
  fallbackManualEvaluation: undefined,
  variant: 'dialog',
  onChangeRut: undefined,
  submitLabel: undefined,
  onLoadingChange: undefined,
};
